import { useState, useEffect } from "react";
import axios from "axios";
import { 
  FileText, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Users, 
  ShieldAlert,
  TrendingUp
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { COLORS, API } from "@/utils/constants";
import { formatCurrency, formatFullCurrency } from "@/utils/helpers";
import SummaryCard from "@/components/common/SummaryCard";

const Dashboard = () => {
  const [summary, setSummary] = useState(null);
  const [savingsSummary, setSavingsSummary] = useState(null);
  const [damagesSummary, setDamagesSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('annual');
  const [showPenalties, setShowPenalties] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [summaryRes, savingsRes, damagesRes] = await Promise.all([
          axios.get(`${API}/compliance/summary`),
          axios.get(`${API}/compliance/savings-summary`),
          axios.get(`${API}/compliance/damages`)
        ]);
        setSummary(summaryRes.data);
        setSavingsSummary(savingsRes.data);
        setDamagesSummary(damagesRes.data);
      } catch (e) {
        console.error(e);
        // Extract detailed error message if available
        let errorMsg = 'Failed to load compliance data';
        if (e.response?.data?.detail) {
          const detail = e.response.data.detail;
          if (typeof detail === 'object') {
            errorMsg = detail.message || detail.error || errorMsg;
            if (detail.suggestion) errorMsg += `. ${detail.suggestion}`;
          } else {
            errorMsg = detail;
          }
        } else if (e.message) {
          errorMsg = `Connection error: ${e.message}`;
        }
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getPeriodLabel = () => {
    if (selectedPeriod === 'monthly') return '/mo';
    if (selectedPeriod === 'quarterly') return '/qtr';
    return '/yr';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2EC4B6]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg p-6 bg-red-900/30 border border-red-800">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-300 font-semibold mb-2">Unable to Load Data</h3>
            <p className="text-red-300/80 text-sm">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 px-4 py-2 bg-red-800/50 hover:bg-red-800 text-red-200 rounded-lg text-sm transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const overCompliant = summary.over_compliant || 0;
  
  // Pie data without "Not Evaluated"
  const pieData = [
    { name: 'Compliant', value: summary.compliant, color: COLORS.compliant },
    { name: 'Non-Compliant', value: summary.non_compliant, color: COLORS.nonCompliant },
  ].filter(d => d.value > 0);

  const stateData = Object.entries(summary.by_state).map(([state, data]) => ({
    state,
    Compliant: data['Compliant'] || 0,
    'Non-Compliant': data['Non-Compliant'] || 0,
  }));

  // Prepare savings chart data
  const savingsChartData = savingsSummary ? [
    { year: '2023', overpayment: savingsSummary.years['2023']?.[selectedPeriod] || 0, isProjected: false },
    { year: '2024', overpayment: savingsSummary.years['2024']?.[selectedPeriod] || 0, isProjected: false },
    { year: '2025', overpayment: savingsSummary.years['2025']?.[selectedPeriod] || 0, isProjected: false },
    { year: '2026', overpayment: savingsSummary.years['2026']?.[selectedPeriod] || 0, isProjected: true },
  ] : [];

  // Period toggle component with penalties - reused in both cards
  const PeriodToggleWithPenalties = ({ color = 'slate' }) => (
    <div className="flex items-center gap-2">
      {/* Penalties Toggle */}
      <button
        onClick={() => setShowPenalties(!showPenalties)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border transition-all duration-200 ${
          showPenalties 
            ? 'bg-red-900/50 border-red-500 text-red-300' 
            : 'bg-slate-700/50 border-slate-600 text-slate-400 hover:text-white hover:border-slate-500'
        }`}
        data-testid="dashboard-show-penalties-toggle"
      >
        <div className={`w-4 h-4 rounded flex items-center justify-center border-2 transition-all duration-200 ${
          showPenalties 
            ? 'bg-red-500 border-red-400' 
            : 'bg-transparent border-slate-500'
        }`}>
          {showPenalties && (
            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
        <span className="text-xs font-medium">Penalties</span>
      </button>
      
      {/* Period Toggle */}
      <div className="flex gap-1">
        {['monthly', 'quarterly', 'annual'].map((period) => (
          <button
            key={period}
            onClick={() => setSelectedPeriod(period)}
            data-testid={`dashboard-period-${period}`}
            className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${
              selectedPeriod === period
                ? color === 'red' ? 'bg-red-600 text-white' : color === 'amber' ? 'bg-amber-600 text-white' : 'bg-slate-600 text-white'
                : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
            }`}
          >
            {period.charAt(0).toUpperCase() + period.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );

  // Period toggle without penalties for Over-Compliance card
  const PeriodToggle = ({ color = 'slate' }) => (
    <div className="flex gap-1">
      {['monthly', 'quarterly', 'annual'].map((period) => (
        <button
          key={period}
          onClick={() => setSelectedPeriod(period)}
          data-testid={`dashboard-period-${period}`}
          className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${
            selectedPeriod === period
              ? color === 'red' ? 'bg-red-600 text-white' : color === 'amber' ? 'bg-amber-600 text-white' : 'bg-slate-600 text-white'
              : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
          }`}
        >
          {period.charAt(0).toUpperCase() + period.slice(1)}
        </button>
      ))}
    </div>
  );

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Header - Compact */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight text-white">Compliance Dashboard</h1>
          <p className="mt-1 flex items-center gap-2 text-sm text-slate-400">
            <span className="inline-block w-1.5 h-1.5 bg-[#2EC4B6] rounded-full animate-pulse"></span>
            Real-time compliance across {Object.keys(summary.by_state).length} states
          </p>
        </div>
        <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-slate-800/50 border border-slate-700">
          <Users className="w-4 h-4 text-[#2EC4B6]" />
          <div>
            <span className="text-lg font-bold text-white">{summary.total_employees?.toLocaleString()}</span>
            <span className="text-xs text-slate-400 ml-1">employees</span>
          </div>
        </div>
      </div>

      {/* Summary Cards - 4 cards without Not Evaluated */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          title="Total Rules"
          value={summary.total_rules}
          subtitle="Paths analyzed"
          icon={FileText}
          bgColor="bg-slate-700"
        />
        <SummaryCard
          title="Compliant"
          value={summary.compliant}
          subtitle={`${overCompliant} over-compliant`}
          icon={CheckCircle2}
          colorClass="text-emerald-400"
          bgColor="bg-emerald-900/50"
        />
        <SummaryCard
          title="Non-Compliant"
          value={summary.non_compliant}
          subtitle="Needs attention"
          icon={XCircle}
          colorClass="text-red-400"
          bgColor="bg-red-900/50"
          pulse={summary.non_compliant > 0}
        />
        <SummaryCard
          title="Over-Compliant"
          value={overCompliant}
          subtitle="Exceeds requirements"
          icon={AlertTriangle}
          colorClass="text-amber-400"
          bgColor="bg-amber-900/50"
          pulse={overCompliant > 0}
        />
      </div>

      {/* Financial Impact Row - Centered, Side by Side */}
      <div className="flex justify-center">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full max-w-6xl">
          {/* Non-Compliance Risk */}
          {damagesSummary && damagesSummary.rules.length > 0 && (() => {
            const yearData2026 = damagesSummary.totals['2026'];
            const periodDivisor = selectedPeriod === 'annual' ? 1 : (selectedPeriod === 'quarterly' ? 4 : 12);
            const backWages2026 = (yearData2026?.back_wages || 0) / periodDivisor;
            const damagesMax2026 = (yearData2026?.damages_max || 0) / periodDivisor;
            const heroAmount = showPenalties ? damagesMax2026 : backWages2026;
            
            return (
              <div className="backdrop-blur-sm bg-slate-800/80 border border-red-900/50 rounded-xl shadow-lg shadow-red-900/10 overflow-hidden">
                {/* Header with Period Toggle */}
                <div className="px-4 py-2 border-b border-red-900/30 bg-red-950/10 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4 text-red-400" />
                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-300">Non-Compliance Risk</h3>
                  </div>
                  <PeriodToggleWithPenalties color="red" />
                </div>
                
                {/* HERO NUMBER - The star of the show */}
                <div className="px-6 py-6 text-center bg-gradient-to-b from-red-950/30 to-transparent">
                  <p className="text-xs text-red-300/70 uppercase tracking-wider mb-1">2026 Projected Risk</p>
                  <p 
                    className="text-5xl font-black text-red-400 tracking-tight"
                    style={{ 
                      textShadow: '0 0 40px rgba(248, 113, 113, 0.4), 0 0 80px rgba(248, 113, 113, 0.2)',
                      filter: 'drop-shadow(0 4px 12px rgba(248, 113, 113, 0.3))'
                    }}
                  >
                    {formatCurrency(heroAmount)}
                  </p>
                  <p className="text-sm text-slate-400 mt-1">
                    {getPeriodLabel().replace('/', 'per ')} • {damagesSummary.total_employees_at_risk} employees at risk
                  </p>
                </div>

                {/* Details Section */}
                <div className="px-4 pb-4">
                  <div className="grid grid-cols-5 gap-3">
                    {/* Historical Year Tiles */}
                    <div className="col-span-2 space-y-1.5">
                      {['2025', '2024', '2023'].map((year) => {
                        const yearData = damagesSummary.totals[year];
                        const backWages = (yearData?.back_wages || 0) / periodDivisor;
                        const damagesMax = (yearData?.damages_max || 0) / periodDivisor;
                        const displayAmount = showPenalties ? damagesMax : backWages;
                        
                        return (
                          <div key={year} className="p-2 rounded-lg bg-slate-900/50 border border-slate-700/50">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-slate-500">{year}</span>
                              <span className="text-sm font-semibold text-red-300/80">{formatCurrency(displayAmount)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Bar Chart */}
                    <div className="col-span-3">
                      <div className="h-36">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart 
                            data={(() => {
                              const byType = {};
                              damagesSummary.rules.forEach(rule => {
                                const type = rule.violation_type;
                                if (!byType[type]) {
                                  byType[type] = { type, backWages2026: 0, total2026: 0 };
                                }
                                byType[type].backWages2026 += (rule.damages_2026?.back_wages || 0) / periodDivisor;
                                byType[type].total2026 += (rule.damages_2026?.total_damages_max || 0) / periodDivisor;
                              });
                              const typeLabels = { 'Overtime': 'OT', 'DoubleTime': 'DT', 'MealBreak': 'Meal', 'RestBreak': 'Rest' };
                              return Object.values(byType).map(d => ({
                                name: typeLabels[d.type] || d.type,
                                amount: showPenalties ? d.total2026 : d.backWages2026,
                              }));
                            })()}
                            margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                            barSize={28}
                          >
                            <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                            <YAxis tick={{ fill: '#94a3b8', fontSize: 9 }} tickFormatter={(value) => formatCurrency(value)} width={45} />
                            <Tooltip 
                              contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #ef4444', color: '#e2e8f0', fontSize: '11px' }}
                              formatter={(value) => [formatFullCurrency(value), '2026']}
                            />
                            <Bar dataKey="amount" fill="#ef4444" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Over-Compliance Cost Impact */}
          {savingsSummary && (() => {
            const amount2026 = savingsSummary.years[2026]?.[selectedPeriod] || 0;
            
            return (
              <div className="backdrop-blur-sm bg-slate-800/80 border border-amber-900/50 rounded-xl shadow-lg shadow-amber-900/10 overflow-hidden">
                {/* Header with Period Toggle */}
                <div className="px-4 py-2 border-b border-amber-900/30 bg-amber-950/10 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-amber-400" />
                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-300">Over-Compliance Cost</h3>
                  </div>
                  <PeriodToggle color="amber" />
                </div>
                
                {/* HERO NUMBER - The star of the show */}
                <div className="px-6 py-6 text-center bg-gradient-to-b from-emerald-950/30 to-transparent">
                  <p className="text-xs text-emerald-300/70 uppercase tracking-wider mb-1">2026 Projected Savings</p>
                  <p 
                    className="text-5xl font-black text-emerald-400 tracking-tight"
                    style={{ 
                      textShadow: '0 0 40px rgba(52, 211, 153, 0.4), 0 0 80px rgba(52, 211, 153, 0.2)',
                      filter: 'drop-shadow(0 4px 12px rgba(52, 211, 153, 0.3))'
                    }}
                  >
                    {formatCurrency(amount2026)}
                  </p>
                  <p className="text-sm text-slate-400 mt-1">
                    {getPeriodLabel().replace('/', 'per ')} • {savingsSummary.rule_count} rules exceeding requirements
                  </p>
                </div>

                {/* Details Section */}
                <div className="px-4 pb-4">
                  <div className="grid grid-cols-5 gap-3">
                    {/* Historical Year Tiles */}
                    <div className="col-span-2 space-y-1.5">
                      {[2025, 2024, 2023].map((year) => {
                        const amount = savingsSummary.years[year]?.[selectedPeriod] || 0;
                        return (
                          <div key={year} className="p-2 rounded-lg bg-slate-900/50 border border-slate-700/50">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-slate-500">{year}</span>
                              <span className="text-sm font-semibold text-amber-300/80">{formatCurrency(amount)}</span>
                            </div>
                          </div>
                        );
                      })}
                      <div className="px-2 py-1.5">
                        <span className="text-[10px] text-slate-500">
                          {(savingsSummary.years[2026]?.savings_percentage || 0).toFixed(1)}% of wages
                        </span>
                      </div>
                    </div>

                    {/* Bar Chart */}
                    <div className="col-span-3">
                      <div className="h-36">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={savingsChartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                            <XAxis dataKey="year" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                            <YAxis tick={{ fill: '#94a3b8', fontSize: 9 }} tickFormatter={(value) => formatCurrency(value)} width={45} />
                            <Tooltip 
                              contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', fontSize: '11px' }}
                              formatter={(value) => [formatFullCurrency(value), `Cost${getPeriodLabel()}`]}
                            />
                            <Bar dataKey="overpayment" radius={[4, 4, 0, 0]}>
                              {savingsChartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.isProjected ? '#34D399' : '#FBBF24'} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Charts Row - Pie + State Bar */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Pie Chart - Smaller */}
        <div className="lg:col-span-2 backdrop-blur-sm bg-slate-800/80 border border-slate-700 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-700 bg-slate-800/50">
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-300">Compliance Status</h3>
          </div>
          <div className="p-4">
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-3 mt-2">
              {pieData.map((item) => (
                <div key={item.name} className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-[10px] text-slate-400">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* State Bar Chart - Larger with bigger labels */}
        <div className="lg:col-span-3 backdrop-blur-sm bg-slate-800/80 border border-slate-700 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-700 bg-slate-800/50">
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-300">Compliance by State</h3>
          </div>
          <div className="p-4">
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stateData} layout="vertical" margin={{ left: 10, right: 10 }}>
                  <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                  <YAxis dataKey="state" type="category" width={100} tick={{ fontSize: 14, fill: '#e2e8f0', fontWeight: 500 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', fontSize: '12px' }} />
                  <Bar dataKey="Compliant" stackId="a" fill={COLORS.compliant} radius={[0, 4, 4, 0]} />
                  <Bar dataKey="Non-Compliant" stackId="a" fill={COLORS.nonCompliant} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
